<template>
  <div class="plugin-expire-manage-container">
    <!-- 搜索区域 -->
    <div class="search-area card">
      <div class="search-filters">
        <div class="filter-row">
          <div class="filter-item">
            <el-input
                v-model="searchForm.productCode"
                placeholder="请输入插件编码"
                clearable
                class="filter-input"
                @keyup.enter="searchExpireRecords"
            />
          </div>

          <div class="filter-item">
            <el-input
                v-model="searchForm.remark"
                placeholder="请输入用户备注"
                clearable
                class="filter-input"
                @keyup.enter="searchExpireRecords"
            />
          </div>

          <div class="search-actions">
            <el-button type="primary" @click="searchExpireRecords" :loading="loading">
              搜索
            </el-button>
            <el-button type="info" @click="resetSearch">
              重置
            </el-button>
            <el-button type="success" @click="showAddDialog">
              新增
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 表格展示区域 -->
    <div class="table-area card">
      <div class="table-header">
        <h3 class="table-title">
          <i class="el-icon-tickets"></i> 插件使用时间管理
          <span class="total-count">(共 {{ total }} 条)</span>
        </h3>
        <div class="table-actions">
          <el-button size="small" @click="refreshData">
            <i class="el-icon-refresh"></i> 刷新
          </el-button>
        </div>
      </div>

      <el-table
          :data="tableData"
          v-loading="loading"
          :cell-style="tableCellStyle"
          :header-cell-style="tableHeaderStyle"
          style="width: 100%"
          border
          stripe
          height="calc(100vh - 300px)"
      >
        <el-table-column label="序号" type="index" width="70" align="center" fixed />

        <el-table-column label="用户昵称" width="150" align="center">
          <template #default="scope">
            <div class="user-nickname">{{ scope.row.nickname || '未知用户' }}</div>
          </template>
        </el-table-column>

        <el-table-column label="插件名称" prop="productName" width="150" align="center" />

        <el-table-column label="插件编码" width="130" align="center">
          <template #default="scope">
            <el-tag size="small" type="info">
              {{ scope.row.productCode }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="插件版本" width="130" align="center">
          <template #default="scope">
            <el-tag size="mini" type="success">v{{ scope.row.productVersion }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="过期时间" width="180" align="center">
          <template #default="scope">
            <div :class="{
              'expired-time': formatExpireStatus(scope.row.productExpireTime).type === 'danger',
              'warning-time': formatExpireStatus(scope.row.productExpireTime).type === 'warning'
            }">
              {{ formatDateTime(scope.row.productExpireTime) }}
            </div>
          </template>
        </el-table-column>

        <el-table-column label="状态" width="100" align="center">
          <template #default="scope">
            <el-tag
                :type="formatExpireStatus(scope.row.productExpireTime).type"
                size="small"
            >
              {{ formatExpireStatus(scope.row.productExpireTime).text }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="用户备注" width="130" align="center">
          <template #default="scope">
            <el-tag v-if="scope.row.remark" size="mini" type="warning">{{ scope.row.remark }}</el-tag>
            <el-tag v-else size="mini" type="danger">暂未备注</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="剩余天数" align="center">
          <template #default="scope">
            <div v-if="scope.row.productExpireTime">
              {{ dayjs(scope.row.productExpireTime).diff(dayjs(), 'day') }}天
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination
            v-model:current-page="currentPage"
            :page-size="pageSize"
            size="small"
            :total="total"
            :pager-count="5"
            layout="prev, pager, next"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- 新增订单对话框 -->
    <el-dialog
        v-model="addDialogVisible"
        title="新增使用时间"
        width="500px"
        :before-close="handleCloseAddDialog"
    >
      <el-form
          ref="addFormRef"
          :model="addForm"
          :rules="addFormRules"
          label-width="120px"
      >
        <el-form-item label="手机号码" prop="phone">
          <el-input
              v-model="addForm.phone"
              placeholder="请输入用户手机号"
              clearable
          />
          <div class="form-tips">请输入11位手机号码（如：13812345678）</div>
        </el-form-item>

        <el-form-item label="购买天数" prop="days">
          <el-input
              v-model="addForm.days"
              placeholder="请输入购买天数"
              clearable
          />
          <div class="form-tips">请输入数字（如：30、90、365）</div>
        </el-form-item>

        <el-form-item label="选择插件" prop="productId">
          <el-select
              v-model="addForm.productId"
              placeholder="请选择插件"
              style="width: 100%"
              clearable
              filterable
              @change="handleProductChange"
          >
            <el-option
                v-for="product in productList"
                :key="product.id"
                :label="product.name"
                :value="product.id"
                :disabled="product.status !== 1"
            >
              <span style="float: left">{{ product.name }}</span>
              <span style="float: right; color: #8492a6; font-size: 13px">
              {{ product.productCode }} v{{ product.version }}
            </span>
            </el-option>
          </el-select>
          <div class="form-tips">选择插件后，将自动填充插件编码和版本号</div>
        </el-form-item>

        <!-- 隐藏字段，用于显示选中的插件信息 -->
        <el-form-item v-if="addForm.productId" label="插件编码">
          <el-input
              v-model="addForm.productCode"
              disabled
          />
        </el-form-item>

        <el-form-item v-if="addForm.productId" label="插件版本">
          <el-input
              v-model="addForm.productVersion"
              disabled
          />
        </el-form-item>
      </el-form>

      <template #footer>
      <span class="dialog-footer">
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleAddOrder" :loading="addLoading">
          确认新增
        </el-button>
      </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import request from '@/utils/request'
import { ElMessage, ElMessageBox } from 'element-plus'
import dayjs from 'dayjs'

// 响应式数据
const searchForm = reactive({
  productCode: '',
  remark: '',
  expireTimeRange: []
})

const tableData = ref([])
const loading = ref(false)
const currentPage = ref(1)
const pageSize = ref(15)
const total = ref(0)

// 订单相关
const addDialogVisible = ref(false)
const addLoading = ref(false)
const addFormRef = ref()
const productList = ref([])

const addForm = reactive({
  phone: '',
  days: null,
  productId: '',  // 新增：产品ID
  productCode: '', // 改为从产品获取
  productVersion: '' // 改为从产品获取
})

// 表单验证规则
const addFormRules = {
  phone: [
    { required: true, message: '请输入手机号', trigger: 'blur' },
    {
      pattern: /^1[3-9]\d{9}$/,
      message: '请输入正确的11位手机号',
      trigger: 'blur'
    }
  ],
  days: [
    { required: true, message: '请输入购买天数', trigger: 'blur' },
    {
      pattern: /^\d+$/,
      message: '请输入有效的数字',
      trigger: 'blur'
    }
  ],
  productId: [
    { required: true, message: '请选择插件', trigger: 'change' }
  ]
}

// 获取产品列表的方法
const loadProductList = async () => {
  try {
    const response = await request.get('/pay/admin/products')
    if (response.code === '200') {
      productList.value = response.data || []
    }
  } catch (error) {
    console.error('获取产品列表失败:', error)
  }
}

// 处理产品选择变化
const handleProductChange = (productId) => {
  if (productId) {
    const selectedProduct = productList.value.find(p => p.id === productId)
    if (selectedProduct) {
      addForm.productCode = selectedProduct.productCode
      addForm.productVersion = selectedProduct.version
    }
  } else {
    addForm.productCode = ''
    addForm.productVersion = ''
  }
}

// 显示新增对话框
const showAddDialog = async () => {
  // 重置表单
  if (addFormRef.value) {
    addFormRef.value.resetFields()
  }
  addForm.phone = ''
  addForm.days = null
  addForm.productId = ''
  addForm.productCode = ''
  addForm.productVersion = ''

  // 加载产品列表
  await loadProductList()

  addDialogVisible.value = true
}

// 关闭新增对话框
const handleCloseAddDialog = (done) => {
  ElMessageBox.confirm('确定要关闭吗？已填写的内容将不会被保存。')
      .then(() => {
        done()
      })
      .catch(() => {
        // 取消关闭
      })
}

// 处理新增订单
const handleAddOrder = () => {
  addFormRef.value.validate(async (valid) => {
    if (!valid) {
      return
    }

    addLoading.value = true
    try {
      const response = await request.post('/pay/admin/createSuccessOrder', addForm)

      if (response.code === '200') {
        if (response.data.success) {
          ElMessage.success({
            message: `订单创建成功！\n订单号：${response.data.orderNo}\n用户：${response.data.nickname || response.data.userName}\n手机号：${response.data.phone}`,
            duration: 6000,
            showClose: true
          })

          // 关闭对话框
          addDialogVisible.value = false

          // 刷新表格数据
          searchExpireRecords()
        } else {
          // ElMessage.error(response.data.message || '创建订单失败')
        }
      } else {
        // ElMessage.error('创建订单失败: ' + (response.msg || '未知错误'))
      }
    } catch (error) {
      console.error('创建订单失败:', error)
      // ElMessage.error('创建订单失败: ' + (error.message || '网络错误'))
    } finally {
      addLoading.value = false
    }
  })
}


// 搜索插件过期记录
const searchExpireRecords = async () => {
  loading.value = true
  try {
    const params = {
      ...searchForm,
      page: currentPage.value,
      size: pageSize.value
    }

    // 处理时间范围
    if (searchForm.expireTimeRange && searchForm.expireTimeRange.length === 2) {
      params.startExpireTime = searchForm.expireTimeRange[0]
      params.endExpireTime = searchForm.expireTimeRange[1]
    }

    // 清理空参数
    Object.keys(params).forEach(key => {
      if (params[key] === '' || params[key] === null || params[key] === undefined ||
          (Array.isArray(params[key]) && params[key].length === 0)) {
        delete params[key]
      }
    })

    const response = await request.get('/plugin/expire-records', { params })

    if (response.code === '200') {
      tableData.value = response.data.list || []
      total.value = response.data.total || 0
      // ElMessage.success(`搜索到 ${total.value} 条记录`)
    } else {
      // ElMessage.error('搜索失败: ' + (response.msg || '未知错误'))
    }
  } catch (error) {
    console.error('搜索插件过期记录失败:', error)
    // ElMessage.error('搜索失败: ' + (error.message || '网络错误'))
  } finally {
    loading.value = false
  }
}

// 重置搜索
const resetSearch = () => {
  searchForm.productCode = ''
  searchForm.remark = ''
  searchForm.expireTimeRange = []
  currentPage.value = 1
  searchExpireRecords()
}

// 刷新数据
const refreshData = () => {
  currentPage.value = 1
  searchExpireRecords()
}

// 表格样式方法
const tableCellStyle = ({ row, column, rowIndex, columnIndex }) => {
  // 可以针对特定列设置样式
  if (column.property === 'loginTime') {
    return {
      color: '#1890ff',
      fontWeight: '500'
    }
  }
  return {}
}

const tableHeaderStyle = ({ row, column, rowIndex, columnIndex }) => {
  return {
    background: '#f8f9fa',
    fontWeight: '600',
    color: '#495057',
    borderBottom: '2px solid #dee2e6'
  }
}

// 格式化日期时间
const formatDateTime = (datetime) => {
  if (!datetime) return '-'
  return dayjs(datetime).format('YYYY-MM-DD hh:mm:ss')
}

// 格式化过期状态
const formatExpireStatus = (expireTime) => {
  if (!expireTime) return { text: '未知', type: 'info' }

  const now = dayjs()
  const expireDate = dayjs(expireTime)

  if (expireDate.isBefore(now)) {
    return { text: '已过期', type: 'danger' }
  } else if (expireDate.diff(now, 'day') <= 7) {
    return { text: '即将过期', type: 'warning' }
  } else {
    return { text: '正常', type: 'success' }
  }
}

// 处理每页大小变化
const handleSizeChange = (newSize) => {
  pageSize.value = newSize
  searchExpireRecords()
}

// 处理页码变化
const handlePageChange = (page) => {
  currentPage.value = page
  searchExpireRecords()
}

// 组件挂载时自动搜索
onMounted(() => {
  searchExpireRecords()
})
</script>

<style scoped>
.plugin-expire-manage-container {
  padding: 20px;
  margin: 0 auto;
}

.card {
  background: #fff;
  border-radius: 5px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.search-area {
  padding: 18px;
  margin-bottom: 10px;
  width: 60%;
}

.filter-row {
  display: flex;
  gap: 24px;
}

.filter-item {
  flex: 1;
}

.filter-input {
  width: 100%;
}

.search-actions {
  display: flex;
  gap: 12px;
}

.table-area {
  padding: 10px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.table-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 8px;
}

.table-title i {
  color: #409eff;
}

.total-count {
  font-size: 14px;
  font-weight: normal;
  color: #64748b;
  margin-left: 8px;
}

.table-actions {
  display: flex;
  gap: 8px;
}

.user-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.user-nickname {
  font-weight: 500;
}

.user-remark {
  margin-top: 4px;
}

.expired-time {
  color: #dc2626;
  font-weight: 500;
}

.warning-time {
  color: #e6a23c;
  font-weight: 500;
}

.pagination-container {
  padding-top: 10px;
  display: flex;
  justify-content: center;
}

/* 表单提示 */
.form-tips {
  font-size: 12px;
  color: #999;
  margin-top: 5px;
}

/* 滚动条样式 */
:deep(.el-table__body-wrapper::-webkit-scrollbar) {
  width: 6px;
  height: 6px;
}

:deep(.el-table__body-wrapper::-webkit-scrollbar-track) {
  background: #f1f1f1;
  border-radius: 3px;
}

:deep(.el-table__body-wrapper::-webkit-scrollbar-thumb) {
  background: #c1c1c1;
  border-radius: 3px;
}

:deep(.el-table__body-wrapper::-webkit-scrollbar-thumb:hover) {
  background: #a8a8a8;
}
</style>